import { Input } from '@mui/base/Input';
import cx from 'classnames';
import { type ChangeEvent } from 'react';
import type {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  UseControllerProps,
} from 'react-hook-form';
import { useController } from 'react-hook-form';
import typography from '~styles/typography.scss';
import styles from './ControlledFormInput.scss';
import { FormTooltip } from './FormTooltip';
import { LayoutVariant } from './sharedTypes';
import { ValidationErrorMessage } from './ValidationErrorMessage';

export interface ControlledFormInputProps<
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
> {
  className?: string;
  control: Control<T>;
  defaultValue?: PathValue<T, Name>;
  disabled?: boolean;
  label: string;
  name: Name;
  placeholder?: string;
  rules?: UseControllerProps<T, Name>['rules'];
  tooltipText?: string;
  type: string;
  variant?: LayoutVariant;
  autoComplete?: 'on' | 'off';
  ignoreOnePass?: boolean;
  readOnly?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus?: () => void;
  labelClassName?: string;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  /** Rendered to the left of the input field, outside the input chrome (does not reduce input width). */
  leadingOutsideInput?: React.ReactNode;
  darkMode?: boolean;
}

export const ControlledFormInput = <
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
>({
  className,
  control,
  defaultValue,
  disabled,
  label,
  name,
  placeholder,
  rules,
  tooltipText,
  type,
  variant = LayoutVariant.Vertical,
  autoComplete = 'off',
  ignoreOnePass = true,
  readOnly = false,
  onBlur,
  onChange,
  onFocus,
  labelClassName = '',
  endAdornment = null,
  startAdornment = null,
  leadingOutsideInput = null,
  darkMode = false,
}: ControlledFormInputProps<T, Name>) => {
  const {
    field: { onChange: _onChange, value, ref },
    fieldState: { error },
  } = useController({
    control,
    defaultValue,
    name,
    rules: {
      ...rules,
      validate: {
        ...rules?.validate,
      },
    },
  });

  const muiInput = (
    <Input
      aria-errormessage={`${name}-error`}
      aria-invalid={!!error}
      autoComplete={autoComplete}
      disabled={disabled}
      readOnly={readOnly}
      id={name}
      onChange={(event) => {
        // react-hook-form's internal `onChange` needs to be called before
        // the external / custom `onChange` to ensure that the value is
        // validated before we do our custom logic.
        _onChange(event);
        onChange?.(event);
      }}
      onWheel={() => (document.activeElement as HTMLInputElement).blur()}
      placeholder={placeholder}
      endAdornment={
        endAdornment ? (
          <span className={styles.endAdornmentWrap}>{endAdornment}</span>
        ) : null
      }
      startAdornment={startAdornment}
      slotProps={{
        root: {
          className: cx(
            styles.inputRoot,
            !!endAdornment && styles.inputRootWithEndAdornment,
          ),
        },
        input: {
          ref,
          'aria-errormessage': `${name}-error`,
          'aria-invalid': !!error,
          // @ts-expect-error MUI base is not ready for 1pass attributes
          'data-1p-ignore': ignoreOnePass,
          'data-lpignore': ignoreOnePass,
          className: cx({
            [typography.t1]: true,
            [styles.inputError]: !!error,
          }),
          onFocus: (e) => {
            onFocus?.();
            if (e.target.type === 'date') {
              try {
                e.target.showPicker?.();
              } catch (onFocusError) {
                if (
                  onFocusError instanceof DOMException &&
                  onFocusError.name === 'NotAllowedError'
                ) {
                  /* Ignore the NotAllowedError.
                   *
                   * Me and my bad, bad homies
                   * Doin' bad, bad things
                   * But it feels so good
                   *
                   * Source: pH-1, “Mr. Bad” (feat. Woo), 2022
                   * */
                } else {
                  throw onFocusError;
                }
              }
            }
          },
        },
      }}
      type={type === 'email' ? 'text' : type}
      value={value || ''}
      {...(onBlur && {
        onBlur,
      })}
    />
  );

  return (
    <div
      className={cx(
        {
          [styles.fieldContainer]: true,
          [styles[variant]]: true,
          [styles.darkMode]: darkMode,
        },
        className,
      )}
    >
      <div className={styles.labelContainer}>
        <label
          className={cx(labelClassName, {
            [styles.labelError]: !!error,
          })}
          htmlFor={name}
        >
          {label}
        </label>
        <FormTooltip label={name} text={tooltipText} />
      </div>
      <div className={styles.inputContainer}>
        <div className={styles.inputShell}>
          {muiInput}
          {leadingOutsideInput ? (
            <div className={styles.leadingOutsideInputSlot}>
              {leadingOutsideInput}
            </div>
          ) : null}
        </div>
        <ValidationErrorMessage error={error} label={label} name={name} />
      </div>
    </div>
  );
};
